/**
 * Live TV player custom element: HLS/DASH streaming, poster overlay, error recovery.
 */
(function () {
  class LiveTvPlayer extends HTMLElement {
    connectedCallback() {
      if (this.isInitialized) return;
      this._initStreamState();
      this._setupPoster();
      this.init(!this.deferStream);
      if (this.dataset.playRequested === "true") {
        delete this.dataset.playRequested;
        this._playVideo();
      }
    }

    disconnectedCallback() {
      this._teardownPoster();
      this.destroy();
    }

    _setupPoster() {
      this._poster = this.querySelector("[data-poster-overlay]");
      this._posterPlay = this.querySelector("[data-poster-play]");
      this._hasStartedPlayback = false;

      if (!this.videoElement) return;

      this._boundUpdatePoster = this._updatePosterVisibility.bind(this);
      this._boundPlayVideo = this._playVideo.bind(this);
      this._boundPosterKeydown = this._handlePosterKeydown.bind(this);
      this._boundWarmPlayerRuntime = this._warmPlayerRuntime.bind(this);

      // Recompute on every event that signals real playback has begun.
      // `play` fires while readyState is still < 2 (no decoded frame), so the
      // overlay must also re-check on `playing`/`canplay`/`loadeddata`/`timeupdate`
      // or it stays stuck on top of a video that is actually playing (audio-only look).
      this._posterEvents = [
        "play",
        "playing",
        "pause",
        "ended",
        "canplay",
        "loadeddata",
        "timeupdate",
        "waiting",
        "stalled",
        "emptied",
      ];
      this._posterEvents.forEach((eventName) => {
        this.videoElement.addEventListener(eventName, this._boundUpdatePoster);
      });

      if (this._poster) {
        this._poster.classList.add("is-visible");
      }

      if (this._posterPlay) {
        this._posterPlay.addEventListener("click", this._boundPlayVideo);
        this._posterPlay.addEventListener("keydown", this._boundPosterKeydown);
        this._posterPlay.addEventListener("pointerover", this._boundWarmPlayerRuntime, {
          once: true,
          passive: true,
        });
        this._posterPlay.addEventListener("touchstart", this._boundWarmPlayerRuntime, {
          once: true,
          passive: true,
        });
        this._posterPlay.addEventListener("focus", this._boundWarmPlayerRuntime, {
          once: true,
          passive: true,
        });
      }

      this._updatePosterVisibility();
    }

    _teardownPoster() {
      if (!this.videoElement) return;

      (this._posterEvents || ["play", "pause"]).forEach((eventName) => {
        this.videoElement.removeEventListener(
          eventName,
          this._boundUpdatePoster
        );
      });

      if (this._posterPlay) {
        this._posterPlay.removeEventListener("click", this._boundPlayVideo);
        this._posterPlay.removeEventListener(
          "keydown",
          this._boundPosterKeydown
        );
        this._posterPlay.removeEventListener(
          "pointerover",
          this._boundWarmPlayerRuntime
        );
        this._posterPlay.removeEventListener(
          "touchstart",
          this._boundWarmPlayerRuntime
        );
        this._posterPlay.removeEventListener("focus", this._boundWarmPlayerRuntime);
      }
    }

    _updatePosterVisibility() {
      if (!this._poster || !this.videoElement) return;
      if (document.hidden) return;

      const video = this.videoElement;

      // Mark playback as started once the stream actually advances. After this,
      // transient live-stream rebuffering (readyState dipping < 2) must NOT bring
      // the overlay back over the playing video — only an explicit pause should.
      if (!video.paused && video.currentTime > 0) {
        this._hasStartedPlayback = true;
      }

      let show;
      if (video.paused) {
        show = true;
      } else if (this._hasStartedPlayback) {
        show = false;
      } else {
        // Pre-first-frame buffering: keep the branded poster until video is ready.
        show = video.readyState < 2;
      }

      this._poster.classList.toggle("is-visible", show);
      this._poster.setAttribute("aria-hidden", show ? "false" : "true");
    }

    _warmPlayerRuntime() {
      if (!this.deferStream || this.shouldUseNativeHls()) return;
      this._ensureHlsLibrary().catch(() => {
        // The click path retries and displays the existing player error handling.
      });
    }

    _playVideo() {
      if (!this.videoElement) return;

      this._pendingUserPlay = true;
      this.userPaused = false;

      if (!this.streamStarted) {
        this.startStream();
        return;
      }

      this._attemptPendingPlayback();
    }

    _attemptPendingPlayback() {
      if (!this.videoElement || (!this.autoplay && !this._pendingUserPlay)) {
        return;
      }

      this.userPaused = false;
      const streamSession = this._streamSession;
      const playPromise = this.videoElement.play();
      if (!playPromise || typeof playPromise.then !== "function") {
        this._pendingUserPlay = false;
        return;
      }

      playPromise
        .then(() => {
          if (!this.isConnected || streamSession !== this._streamSession) return;
          this.isPlaying = true;
          this._pendingUserPlay = false;
          this.isInitialLoad = false;
        })
        .catch((/** @type {unknown} */ error) => {
          if (!this.isConnected || streamSession !== this._streamSession) return;
          // Keep the request pending while the manifest/metadata is still loading.
          // It is retried by MANIFEST_PARSED or loadedmetadata.
          if (this.videoElement && this.videoElement.readyState >= 2) {
            this._pendingUserPlay = false;
            console.warn("[LiveTV] Playback request failed:", error);
          }
        });
    }

    async startStream() {
      if (this.streamStarted || !this.isConnected || !this.videoElement) return;
      this.streamStarted = true;
      const streamSession = this._streamSession;

      if (this.shouldUseNativeHls()) {
        this.waitForHlsJsAndInit();
        return;
      }

      try {
        await this._ensureHlsLibrary();
        if (!this.isConnected || streamSession !== this._streamSession) return;
        this.waitForHlsJsAndInit();
      } catch (error) {
        if (streamSession !== this._streamSession) return;
        this.streamStarted = false;
        this.isLoading = false;
        console.error("[LiveTV] Unable to load the player runtime:", error);
      }
    }

    _ensureHlsLibrary() {
      if (
        this.shouldUseNativeHls() ||
        (window.Hls && typeof window.Hls.isSupported === "function")
      ) {
        return Promise.resolve();
      }

      if (!this.hlsLibraryUrl) {
        return Promise.reject(new Error("HLS library URL is missing"));
      }

      if (window.__tjcHlsLibraryPromise) {
        return window.__tjcHlsLibraryPromise;
      }

      window.__tjcHlsLibraryPromise = new Promise((resolve, reject) => {
        const script = document.createElement("script");
        script.src = this.hlsLibraryUrl;
        script.async = true;
        script.fetchPriority = "high";
        script.dataset.tjcHlsLibrary = "true";
        script.onload = () => {
          if (window.Hls && typeof window.Hls.isSupported === "function") {
            resolve();
          } else {
            window.__tjcHlsLibraryPromise = null;
            reject(new Error("HLS library loaded without exposing window.Hls"));
          }
        };
        script.onerror = () => {
          window.__tjcHlsLibraryPromise = null;
          reject(new Error("HLS library request failed"));
        };
        document.head.appendChild(script);
      });

      return window.__tjcHlsLibraryPromise;
    }

    _handlePosterKeydown(event) {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        this._playVideo();
      }
    }


    _initStreamState() {
    this._streamSession = (this._streamSession || 0) + 1;
    this.videoElement = this.querySelector("video");
    this.hlsUrl = this.dataset.hlsUrl;
    this.dashUrl = this.dataset.dashUrl;
    this.muted = this.dataset.muted === "true";
    // Sound-on playback requires intent; configured muted autoplay is preserved.
    this.autoplay = this.dataset.autoplay === "true" && this.muted;
    this.deferStream = !this.autoplay;
    this.hlsLibraryUrl = this.dataset.hlsLibraryUrl || "";
    this.streamStarted = false;
    this._nativeLoadStarted = false;
    this._pendingUserPlay = false;
    this.hlsInstance = null;
    this.retryCount = 0;
    this.maxRetries = 3;
    this.retryDelay = 1500;
    this.reconnectTimer = null;
    this.isInitialized = false;
    this.errorCount = 0;
    this.maxErrorCount = 10;
    this.lastErrorTime = null;
    this.demuxerErrorCount = 0;
    this.maxDemuxerRetries = 3;
    this.streamValidationDelay = 1000;
    this.isLoading = false;
    this.lastRetryTime = 0;
    this.minRetryInterval = 2000;
    this.maxTotalRetries = 5;
    this.totalRetryCount = 0;
    this.cooldownUntil = 0;
    this.cooldownDuration = 3000;
    this.abortController = null;
    this.isPlaying = !!this.videoElement && !this.videoElement.paused;
    this.userPaused = false;
    this.currentSrc = null;
    this.isInitialLoad = true;
    this.initialLoadStartTime = 0;
    this.initialLoadGracePeriod = 10000;
    this.bufferingLogCount = 0;
    this.stallCount = 0;
    this.lastStallTime = 0;
    this.stallWindow = 30000;
    this.maxStallsBeforeRecovery = 3;
    this.isRestoringSrc = false;
    this.errorHandlerDisabled = false;
    this.playbackMode = null;
    this.lastDemuxerRecoverAt = 0;
    this.handleOnline = this.handleOnline.bind(this);
    this.handleOffline = this.handleOffline.bind(this);
    this.handlePlaybackPlay = this.handlePlaybackPlay.bind(this);
    this.handlePlaybackPause = this.handlePlaybackPause.bind(this);
    this._boundVideoError = this.handleVideoError.bind(this);
    this._boundVideoStalled = this.handleVideoStalled.bind(this);
    this._boundVideoWaiting = this.handleVideoWaiting.bind(this);
  }

  handlePlaybackPlay() {
    this.isPlaying = true;
    this.userPaused = false;
    this._pendingUserPlay = false;
    this._updatePosterVisibility();
    this._seekToLiveEdgeIfBehind();
  }

  /** Keep live TV at the broadcast edge after resume (avoid playing stale buffered segments). */
  _seekToLiveEdgeIfBehind() {
    if (
      !this.hlsInstance ||
      !this.videoElement ||
      this.playbackMode !== "hlsjs"
    ) {
      return;
    }

    const liveEdge = this.hlsInstance.liveSyncPosition;
    if (liveEdge == null || !Number.isFinite(liveEdge)) {
      return;
    }

    const delay = liveEdge - this.videoElement.currentTime;
    if (delay > 4) {
      this.videoElement.currentTime = liveEdge;
    }
  }

  handlePlaybackPause() {
    this.isPlaying = false;
    if (!this._pendingUserPlay) {
      this.userPaused = true;
    }
    this._updatePosterVisibility();
  }

  init(startStream = true) {
    if (this.isInitialized || !this.isConnected) {
      console.warn("[LiveTV] Player already initialized or destroyed");
      return;
    }

    if (!this.videoElement || !this.hlsUrl) {
      console.warn("[LiveTV] Video element or HLS URL not found");
      return;
    }

    this.isInitialized = true;

    // Set video attributes
    this.videoElement.muted = this.muted;
    if (!this.autoplay) {
      this.videoElement.removeAttribute("autoplay");
    }

    // Add event listeners for network and visibility changes
    window.addEventListener("online", this.handleOnline);
    window.addEventListener("offline", this.handleOffline);

    this.videoElement.addEventListener("play", this.handlePlaybackPlay);
    this.videoElement.addEventListener("pause", this.handlePlaybackPause);

    // Add video error handlers
    this.videoElement.addEventListener(
      "error",
      this._boundVideoError
    );
    this.videoElement.addEventListener(
      "stalled",
      this._boundVideoStalled
    );
    this.videoElement.addEventListener(
      "waiting",
      this._boundVideoWaiting
    );

    // Keep the player/UI initialized, but do not connect a deferred live stream
    // until Play. The separate Current On-Air app block remains fully eager.
    if (startStream) {
      this.startStream();
    }
  }

  /**
   * Safari/iOS only. Chromium may report HLS via canPlayType but live MSE often
   * fails with DEMUXER_ERROR_COULD_NOT_PARSE on direct video src.
   * @returns {boolean}
   */
  shouldUseNativeHls() {
    if (!this.videoElement) {
      return false;
    }

    const ua = navigator.userAgent;
    const isChromium =
      /Chrome|Chromium|Edg\//.test(ua) && !/Firefox/i.test(ua);
    if (isChromium) {
      return false;
    }

    const isSafari =
      /Safari/i.test(ua) &&
      !/Chrome|Chromium|Edg\//i.test(ua) &&
      /AppleWebKit/i.test(ua);
    if (!isSafari) {
      return false;
    }

    return (
      this.videoElement.canPlayType("application/vnd.apple.mpegurl") !== ""
    );
  }

  /**
   * @returns {boolean}
   */
  isHlsJsSupported() {
    return (
      !!window.Hls &&
      typeof window.Hls.isSupported === "function" &&
      window.Hls.isSupported()
    );
  }

  /**
   * @param {number} errorCode
   * @param {string} errorMessage
   * @returns {boolean}
   */
  isVideoDemuxerError(errorCode, errorMessage) {
    return (
      errorMessage.includes("DEMUXER_ERROR") ||
      errorMessage.includes("COULD_NOT_PARSE") ||
      errorMessage.includes("PipelineStatus") ||
      errorMessage.includes("demuxer") ||
      errorMessage.includes("parse") ||
      (errorCode === 4 && !!this.videoElement?.src)
    );
  }

  /**
   * @param {number} delayMs
   */
  tryHlsJsMediaRecovery(delayMs) {
    const delay = delayMs || 0;
    setTimeout(() => {
      if (!this.isConnected || !this.hlsInstance) {
        return;
      }
      try {
        this.hlsInstance.recoverMediaError();
        this.hlsInstance.startLoad();
      } catch (/** @type {unknown} */ error) {
        console.warn("[LiveTV] HLS.js media recovery failed:", error);
      }
    }, delay);
  }

  /**
   * Video element demuxer errors while HLS.js (or MSE) is the intended path.
   */
  handleVideoDemuxerWithHlsJs() {
    const now = Date.now();
    const timeSinceInitialLoad = now - this.initialLoadStartTime;
    const inGracePeriod =
      this.isInitialLoad &&
      timeSinceInitialLoad < this.initialLoadGracePeriod;

    if (inGracePeriod) {
      console.log(
        `[LiveTV] Demuxer error during initial load (grace period: ${Math.ceil(
          (this.initialLoadGracePeriod - timeSinceInitialLoad) / 1000
        )}s remaining). HLS.js will recover.`
      );
      if (this.hlsInstance) {
        this.tryHlsJsMediaRecovery(500);
      }
      return;
    }

    if (now - this.lastDemuxerRecoverAt < this.minRetryInterval) {
      return;
    }
    this.lastDemuxerRecoverAt = now;

    this.demuxerErrorCount++;
    this.totalRetryCount++;
    console.warn(
      `[LiveTV] Demuxer error on video element (${this.demuxerErrorCount}/${this.maxDemuxerRetries}, total: ${this.totalRetryCount}/${this.maxTotalRetries})`
    );

    if (this.totalRetryCount >= this.maxTotalRetries) {
      console.error(
        `[LiveTV] Max total retries (${this.maxTotalRetries}) reached. Entering cooldown.`
      );
      this.cooldownUntil = Date.now() + this.cooldownDuration;
      this.totalRetryCount = 0;
      this.demuxerErrorCount = 0;
      this.loadFallback();
      return;
    }

    if (this.hlsInstance) {
      if (this.demuxerErrorCount < this.maxDemuxerRetries) {
        const retryDelay = Math.min(
          this.retryDelay * (this.demuxerErrorCount + 1),
          3000
        );
        console.log(
          `[LiveTV] HLS.js recovering after ${retryDelay}ms (stream parse glitch)`
        );
        this.tryHlsJsMediaRecovery(retryDelay);
      } else {
        console.error(
          "[LiveTV] Max demuxer retries reached, trying fallback"
        );
        this.demuxerErrorCount = 0;
        this.loadFallback();
      }
      return;
    }

    if (this.demuxerErrorCount < this.maxDemuxerRetries) {
      const retryDelay = Math.min(
        this.retryDelay * (this.demuxerErrorCount + 1),
        3000
      );
      console.log(
        `[LiveTV] Re-initializing HLS.js after ${retryDelay}ms (no active instance)`
      );
      this.destroyHlsInstance();
      setTimeout(() => {
        if (this.isConnected) {
          this.loadHlsJs();
        }
      }, retryDelay);
    } else {
      console.error(
        "[LiveTV] Max demuxer retries reached, trying fallback"
      );
      this.demuxerErrorCount = 0;
      this.loadFallback();
    }
  }

  waitForHlsJsAndInit() {
    if (!this.isConnected || !this.streamStarted) return;
    if (this.shouldUseNativeHls()) {
      this.loadNativeHLS();
      return;
    }

    // Check if HLS.js is already loaded
    if (
      window.Hls &&
      typeof window.Hls.isSupported === "function" &&
      window.Hls.isSupported()
    ) {
      this.loadHlsJs();
      return;
    }

    // Wait for HLS.js to load (with timeout)
    const maxWaitTime = 10000; // 10 seconds
    const startTime = Date.now();
    const checkInterval = 100; // Check every 100ms
    const streamSession = this._streamSession;
    clearInterval(this._hlsWaitTimer);

    this._hlsWaitTimer = setInterval(() => {
      if (!this.isConnected || streamSession !== this._streamSession) {
        clearInterval(this._hlsWaitTimer);
        return;
      }
      if (
        window.Hls &&
        typeof window.Hls.isSupported === "function" &&
        window.Hls.isSupported()
      ) {
        clearInterval(this._hlsWaitTimer);
        console.log("[LiveTV] HLS.js loaded, initializing player");
        this.loadHlsJs();
      } else if (Date.now() - startTime > maxWaitTime) {
        clearInterval(this._hlsWaitTimer);
        console.warn(
          "[LiveTV] HLS.js not loaded after timeout, using fallback"
        );
        this.loadFallback();
      }
    }, checkInterval);
  }

  /**
   * Cancel any pending requests
   */
  cancelPendingRequests() {
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
      console.log("[LiveTV] Cancelled pending requests");
    }
  }

  /**
   * Validate HLS stream URL before loading
   * @param {string} url
   * @returns {Promise<boolean>}
   */
  async validateStreamUrl(url) {
    // Cancel any previous validation requests
    this.cancelPendingRequests();

    // Create new abort controller for this request
    this.abortController = new AbortController();

    try {
      const response = await fetch(url, {
        method: "HEAD",
        mode: "no-cors", // Use no-cors to avoid CORS issues, but we can't read response
        signal: this.abortController.signal,
      });
      return true;
    } catch (error) {
      // Check if request was aborted
      if (error.name === "AbortError") {
        console.log("[LiveTV] Stream validation cancelled");
        return false;
      }

      // Try with GET request to manifest
      try {
        const response = await fetch(url, {
          method: "GET",
          headers: {
            Accept: "application/vnd.apple.mpegurl, application/x-mpegURL, */*",
          },
          signal: this.abortController.signal,
        });
        if (response.ok) {
          const text = await response.text();
          // Check if it's a valid HLS manifest
          return text.includes("#EXTM3U") || text.includes("#EXT-X");
        }
      } catch (fetchError) {
        if (fetchError.name === "AbortError") {
          console.log("[LiveTV] Stream validation cancelled");
          return false;
        }
        console.warn("[LiveTV] Stream validation failed:", fetchError);
      }
      return false;
    } finally {
      this.abortController = null;
    }
  }

  loadNativeHLS() {
    if (!this.isConnected || !this.streamStarted) return;
    if (!this.videoElement || !this.hlsUrl) {
      console.error(
        "[LiveTV] Cannot load native HLS: missing video element or URL"
      );
      return;
    }

    // Prevent multiple simultaneous loads
    if (this.isLoading) {
      console.warn("[LiveTV] Already loading, skipping duplicate request");
      return;
    }

    // Check cooldown period
    const now = Date.now();
    if (now < this.cooldownUntil) {
      const remaining = Math.ceil((this.cooldownUntil - now) / 1000);
      console.warn(
        `[LiveTV] In cooldown period, ${remaining}s remaining. Skipping load.`
      );
      return;
    }

    // Check throttling - ensure minimum time between retries
    if (now - this.lastRetryTime < this.minRetryInterval) {
      const waitTime = this.minRetryInterval - (now - this.lastRetryTime);
      console.log(`[LiveTV] Throttling: waiting ${waitTime}ms before retry`);
      setTimeout(() => this.loadNativeHLS(), waitTime);
      return;
    }

    // Check total retry limit
    if (this.totalRetryCount >= this.maxTotalRetries) {
      console.error(
        `[LiveTV] Max total retries (${this.maxTotalRetries}) reached. Entering cooldown.`
      );
      this.cooldownUntil = now + this.cooldownDuration;
      this.totalRetryCount = 0; // Reset after cooldown
      return;
    }

    this.isLoading = true;
    this.lastRetryTime = now;
    this.isInitialLoad = true;
    this.initialLoadStartTime = now;
    const firstNativeLoad = !this._nativeLoadStarted;
    this._nativeLoadStarted = true;
    if (!this.userPaused && this._hasStartedPlayback) this._pendingUserPlay = true;
    this.playbackMode = "native";
    console.log("[LiveTV] Using native HLS support");
    const streamSession = this._streamSession;
    if (this._nativeLoadCleanup) this._nativeLoadCleanup();

    const loadVideo = async () => {
      if (!this.videoElement || !this.hlsUrl) {
        this.isLoading = false;
        return;
      }

      // Keep first Play synchronous for native Safari. Only an existing source
      // that needs recovery/replacement requires the old reset delay.
      const existingSource = this.videoElement.getAttribute("src");
      const adoptBootstrapSource = firstNativeLoad && existingSource === this.hlsUrl && !this.videoElement.error;
      const needsClear = existingSource && !adoptBootstrapSource;

      if (needsClear) {
        // Clear any existing source first
        this.videoElement.removeAttribute("src");
        this.videoElement.load();
        // Reduced wait time for faster loading
        await new Promise((resolve) => setTimeout(resolve, 200));
      }
      if (!this.isConnected || streamSession !== this._streamSession) return;

      // Set the source
      if (this.videoElement.getAttribute("src") !== this.hlsUrl) {
        this.videoElement.src = this.hlsUrl;
      }
      this.currentSrc = this.hlsUrl;

      const onLoadedMetadata = () => {
        if (!this.videoElement) {
          this.isLoading = false;
          return;
        }
        console.log("[LiveTV] Native HLS loaded successfully");
        this.videoElement.removeEventListener(
          "loadedmetadata",
          onLoadedMetadata
        );
        this.videoElement.removeEventListener("error", onError);

        // Reset counters on success
        this.retryCount = 0;
        this.demuxerErrorCount = 0;
        this.totalRetryCount = 0;
        this.isLoading = false;
        this.isPlaying = !this.videoElement.paused;
        this.isInitialLoad = false; // Initial load complete
        this.bufferingLogCount = 0; // Reset buffering log count

        if (this.autoplay || this._pendingUserPlay) {
          this._attemptPendingPlayback();
        }
      };

      const onError = (/** @type {Event} */ error) => {
        if (!this.videoElement) {
          this.isLoading = false;
          return;
        }
        console.error("[LiveTV] Native HLS load error:", error);

        const errorCode = this.videoElement.error?.code;
        const errorMessage = this.videoElement.error?.message || "";

        this.videoElement.removeEventListener(
          "loadedmetadata",
          onLoadedMetadata
        );
        this.videoElement.removeEventListener("error", onError);

        this.errorHandlerDisabled = true;
        setTimeout(() => {
          this.errorHandlerDisabled = false;
        }, 300);

        // Increment total retry count
        this.totalRetryCount++;
        this.isLoading = false;

        const isDemuxerError = this.isVideoDemuxerError(
          errorCode || 0,
          errorMessage
        );

        if (isDemuxerError) {
          this.demuxerErrorCount++;
          console.warn(
            `[LiveTV] Demuxer error detected (${this.demuxerErrorCount}/${this.maxDemuxerRetries}, total: ${this.totalRetryCount}/${this.maxTotalRetries})`
          );

          if (
            this.demuxerErrorCount < this.maxDemuxerRetries &&
            this.totalRetryCount < this.maxTotalRetries
          ) {
            // Clear source and wait longer before retry for demuxer errors
            this.videoElement.src = "";
            this.videoElement.load();
            const retryDelay = Math.min(
              this.retryDelay * (this.demuxerErrorCount + 1) * 2,
              10000
            ); // Max 10 seconds delay
            console.log(
              `[LiveTV] Retrying after ${retryDelay}ms to allow stream to stabilize`
            );
            setTimeout(() => {
              if (!!this.isConnected) {
                this.loadNativeHLS();
              }
            }, retryDelay);
          } else {
            console.error(
              "[LiveTV] Max demuxer retries reached, trying fallback"
            );
            this.demuxerErrorCount = 0;
            this.loadFallback();
          }
        } else if (
          this.retryCount < this.maxRetries &&
          this.totalRetryCount < this.maxTotalRetries
        ) {
          this.retryCount++;
          const retryDelay = Math.min(this.retryDelay * this.retryCount, 5000); // Max 5 seconds delay
          console.log(
            `[LiveTV] Retrying native HLS load (${this.retryCount}/${this.maxRetries}, total: ${this.totalRetryCount}/${this.maxTotalRetries})`
          );
          setTimeout(() => {
            if (!!this.isConnected) {
              this.loadNativeHLS();
            }
          }, retryDelay);
        } else {
          console.error("[LiveTV] Max retries reached, trying fallback");
          this.loadFallback();
        }
      };

      this.videoElement.addEventListener("loadedmetadata", onLoadedMetadata, {
        once: true,
      });
      this.videoElement.addEventListener("error", onError, { once: true });
      const video = this.videoElement;
      this._nativeLoadCleanup = () => {
        video.removeEventListener("loadedmetadata", onLoadedMetadata);
        video.removeEventListener("error", onError);
        this._nativeLoadCleanup = null;
      };
      // A native first click can finish loading before the deferred runtime
      // upgrades this element. The preserved source will not emit metadata again.
      if (adoptBootstrapSource && video.readyState >= 1) {
        onLoadedMetadata();
      } else if (this.autoplay || this._pendingUserPlay) {
        this._attemptPendingPlayback();
      }
    };

    loadVideo();
  }

  async loadHlsJs() {
    if (!this.isConnected || !this.streamStarted || !this.videoElement || !this.hlsUrl) {
      console.error("[LiveTV] Cannot load HLS.js: missing requirements");
      return;
    }

    // Prevent multiple simultaneous loads
    if (this.isLoading) {
      console.warn(
        "[LiveTV] Already loading HLS.js, skipping duplicate request"
      );
      return;
    }

    // Check cooldown period
    const now = Date.now();
    if (now < this.cooldownUntil) {
      const remaining = Math.ceil((this.cooldownUntil - now) / 1000);
      console.warn(
        `[LiveTV] In cooldown period, ${remaining}s remaining. Skipping HLS.js load.`
      );
      return;
    }

    // Check throttling
    if (now - this.lastRetryTime < this.minRetryInterval) {
      const waitTime = this.minRetryInterval - (now - this.lastRetryTime);
      console.log(
        `[LiveTV] Throttling HLS.js: waiting ${waitTime}ms before retry`
      );
      setTimeout(() => this.loadHlsJs(), waitTime);
      return;
    }

    // Check total retry limit
    if (this.totalRetryCount >= this.maxTotalRetries) {
      console.error(
        `[LiveTV] Max total retries (${this.maxTotalRetries}) reached. Entering cooldown.`
      );
      this.cooldownUntil = now + this.cooldownDuration;
      this.totalRetryCount = 0;
      return;
    }

    if (
      !window.Hls ||
      typeof window.Hls.isSupported !== "function" ||
      !window.Hls.isSupported()
    ) {
      console.warn("[LiveTV] HLS.js not supported, using fallback");
      this.loadFallback();
      return;
    }

    this.isLoading = true;
    this.lastRetryTime = now;
    this.isInitialLoad = true;
    this.initialLoadStartTime = now;
    this.playbackMode = "hlsjs";
    console.log("[LiveTV] Using HLS.js");

    // Destroy existing instance if any
    if (this.hlsInstance) {
      this.destroyHlsInstance();
    }

    const streamSession = this._streamSession;
    if (!this.userPaused && this._hasStartedPlayback) this._pendingUserPlay = true;
    // Only clear video source if needed (not playing or different source)
    if (this.videoElement) {
      const needsClear =
        !this.videoElement.src ||
        this.videoElement.src !== this.hlsUrl ||
        (!this.isPlaying && this.videoElement.readyState === 0);

      if (needsClear) {
        this.videoElement.src = "";
        this.videoElement.load();
        // Reduced wait time for faster loading
        await new Promise((resolve) => setTimeout(resolve, 200));
      }
    }
    if (!this.isConnected || streamSession !== this._streamSession) return;

    try {
      /** @type {Hls} */
      const HlsClass = window.Hls;
      this.hlsInstance = new HlsClass({
        enableWorker: true,
        capLevelToPlayerSize: true,
        ignoreDevicePixelRatio: false,
        // The source is plain HLS v3 (4s TS segments, no EXT-X-PART, no
        // EXT-X-SERVER-CONTROL), so low-latency mode has nothing to act on.
        lowLatencyMode: false,
        // On a live stream the forward buffer can never exceed the distance from
        // the live edge - video past that point has not been broadcast yet. So
        // latency IS the stall protection, and liveSyncDuration is the one dial
        // that sets both. At 8s we sit two 4s segments back: enough to absorb a
        // late fragment, close to the ~4-8s floor imposed by the encoder having
        // to finish writing a segment before it can publish it.
        // (The old liveSyncDurationCount: 1 asked for a 4s gap, i.e. no cushion
        // at all, which is why every hiccup surfaced as bufferStalledError.)
        liveSyncDuration: 8,
        liveMaxLatencyDuration: 30,
        maxBufferLength: 12,
        maxMaxBufferLength: 30,
        maxBufferSize: 30 * 1000 * 1000,
        backBufferLength: 10,
        maxBufferHole: 0.5,
        highBufferWatchdogPeriod: 2,
        nudgeOffset: 0.1,
        nudgeMaxRetry: 5,
        maxFragLookUpTolerance: 0.25,
        liveDurationInfinity: true,
        // Catch-up: after a hiccup, play slightly fast to return to the 8s mark
        // instead of permanently drifting further behind.
        maxLiveSyncPlaybackRate: 1.1,
        fragLoadingTimeOut: 20000,
        manifestLoadingTimeOut: 15000, // Increased timeout
        manifestLoadingMaxRetry: 6, // Increased retries
        manifestLoadingRetryDelay: 2000, // Longer delay between retries
        levelLoadingTimeOut: 15000,
        levelLoadingMaxRetry: 6,
        levelLoadingRetryDelay: 2000,
      });

      // Set up event listeners
      this.setupHlsEventListeners();

      // Load the source
      if (this.hlsInstance && this.hlsUrl && this.videoElement) {
        // Ensure video element doesn't have src set when HLS.js is managing it
        // HLS.js will manage the source internally
        if (this.videoElement.src && this.videoElement.src !== this.hlsUrl) {
          this.errorHandlerDisabled = true;
          this.videoElement.src = "";
          this.videoElement.load();
          setTimeout(() => {
            this.errorHandlerDisabled = false;
          }, 200);
        }

        // Attach media first
        this.hlsInstance.attachMedia(this.videoElement);
        // Then load source
        this.hlsInstance.loadSource(this.hlsUrl);
        this.currentSrc = this.hlsUrl;
      }
    } catch (/** @type {unknown} */ error) {
      console.error("[LiveTV] Error creating HLS instance:", error);
      this.isLoading = false;
      this.loadFallback();
    }
  }

  setupHlsEventListeners() {
    if (!this.hlsInstance || !window.Hls) return;

    const HlsEvents = window.Hls.Events;

    // Manifest parsed successfully
    this.hlsInstance.on(HlsEvents.MANIFEST_PARSED, () => {
      console.log("[LiveTV] HLS.js manifest parsed successfully");
      // Reset all counters on success
      this.retryCount = 0;
      this.errorCount = 0;
      this.demuxerErrorCount = 0;
      this.totalRetryCount = 0;
      this.isLoading = false;
      this.cooldownUntil = 0;
      this.isPlaying = false; // Will be set to true when play() succeeds
      this.isInitialLoad = false; // Initial load complete
      this.bufferingLogCount = 0; // Reset buffering log count
      this.stallCount = 0;
      this.lastStallTime = 0;

      // Start only after explicit Play (or when autoplay is intentionally enabled).
      setTimeout(() => {
        if (this.autoplay || this._pendingUserPlay) {
          this._attemptPendingPlayback();
        }
      }, 200);
    });

    // Error handling
    this.hlsInstance.on(
      HlsEvents.ERROR,
      (/** @type {string} */ event, /** @type {HlsErrorData} */ data) => {
        this.handleHlsError(data);
      }
    );

    // Level switched (quality change)
    this.hlsInstance.on(
      HlsEvents.LEVEL_SWITCHED,
      (/** @type {string} */ event, /** @type {unknown} */ data) => {
        const levelData = /** @type {{ level?: number }} */ (data);
        if (levelData.level !== undefined) {
          console.log("[LiveTV] Quality level switched:", levelData.level);
        }
      }
    );

    // Fragment loaded
    this.hlsInstance.on(HlsEvents.FRAG_LOADED, () => {
      // Reset error count on successful fragment load
      if (this.errorCount > 0) {
        this.errorCount = Math.max(0, this.errorCount - 1);
      }
    });
  }

  /**
   * @param {HlsErrorData} data
   */
  handleHlsError(data) {
    if (!data) return;

    const errorType = data.type;
    const errorDetails = data.details;
    const isFatal = data.fatal;

    if (!isFatal) {
      this.handleNonFatalHlsError(errorDetails || "");
      return;
    }

    console.error("[LiveTV] HLS.js error:", {
      type: errorType,
      details: errorDetails,
      fatal: isFatal,
      error: data.error,
    });

    if (!window.Hls) return;

    const HlsErrorTypes = window.Hls.ErrorTypes;

    // Handle fatal errors
    switch (errorType) {
      case HlsErrorTypes.NETWORK_ERROR:
        console.log("[LiveTV] Fatal network error, attempting recovery");
        this.errorCount++;
        this.totalRetryCount++;

        // Check total retry limit
        if (this.totalRetryCount >= this.maxTotalRetries) {
          console.error(
            `[LiveTV] Max total retries (${this.maxTotalRetries}) reached. Entering cooldown.`
          );
          this.cooldownUntil = Date.now() + this.cooldownDuration;
          this.totalRetryCount = 0;
          this.loadFallback();
          break;
        }

        if (this.errorCount < this.maxErrorCount) {
          // Try to recover with throttling
          const now = Date.now();
          if (now - this.lastRetryTime >= this.minRetryInterval) {
            try {
              if (this.hlsInstance) {
                this.lastRetryTime = now;
                this.hlsInstance.startLoad();
              }
            } catch (error) {
              console.error("[LiveTV] Recovery attempt failed:", error);
              this.reconnect();
            }
          } else {
            const waitTime = this.minRetryInterval - (now - this.lastRetryTime);
            console.log(`[LiveTV] Throttling recovery: waiting ${waitTime}ms`);
            setTimeout(() => {
              if (this.hlsInstance && !!this.isConnected) {
                try {
                  this.hlsInstance.startLoad();
                } catch (error) {
                  console.error("[LiveTV] Recovery attempt failed:", error);
                  this.reconnect();
                }
              }
            }, waitTime);
          }
        } else {
          console.error("[LiveTV] Too many network errors, reconnecting");
          this.reconnect();
        }
        break;

      case HlsErrorTypes.MEDIA_ERROR:
        console.log("[LiveTV] Fatal media error, attempting recovery");
        const errorDetails = data.details || "";
        const isDemuxerError =
          errorDetails.includes("demuxer") ||
          errorDetails.includes("parse") ||
          errorDetails.includes("DEMUXER_ERROR") ||
          errorDetails.includes("COULD_NOT_PARSE");

        if (isDemuxerError) {
          // Check if we're in initial load grace period
          const timeSinceInitialLoad = Date.now() - this.initialLoadStartTime;
          const inGracePeriod =
            this.isInitialLoad &&
            timeSinceInitialLoad < this.initialLoadGracePeriod;

          if (inGracePeriod) {
            console.log(
              `[LiveTV] Demuxer error during initial load (grace period: ${Math.ceil(
                (this.initialLoadGracePeriod - timeSinceInitialLoad) / 1000
              )}s remaining). This is normal, attempting recovery...`
            );
            // During grace period, try recovery first before destroying
            try {
              if (this.hlsInstance) {
                // Try to recover media error first
                this.hlsInstance.recoverMediaError();
                // Also try to restart load after a delay
                setTimeout(() => {
                  if (this.hlsInstance && !!this.isConnected) {
                    try {
                      this.hlsInstance.startLoad();
                    } catch (error) {
                      // Ignore errors during recovery attempts
                    }
                  }
                }, 1500);
              }
            } catch (error) {
              // Silently ignore recovery errors during grace period
            }
            break; // Don't count this as a retry during grace period
          }

          this.demuxerErrorCount++;
          this.totalRetryCount++;
          console.warn(
            `[LiveTV] Demuxer error in HLS.js (${this.demuxerErrorCount}/${this.maxDemuxerRetries}, total: ${this.totalRetryCount}/${this.maxTotalRetries})`
          );

          // Check total retry limit
          if (this.totalRetryCount >= this.maxTotalRetries) {
            console.error(
              `[LiveTV] Max total retries (${this.maxTotalRetries}) reached. Entering cooldown.`
            );
            this.cooldownUntil = Date.now() + this.cooldownDuration;
            this.totalRetryCount = 0;
            this.demuxerErrorCount = 0;
            this.loadFallback();
            break;
          }

          if (this.demuxerErrorCount < this.maxDemuxerRetries) {
            // Try recovery first before destroying
            try {
              if (this.hlsInstance) {
                this.hlsInstance.recoverMediaError();
                // Wait a bit and restart load
                setTimeout(() => {
                  if (this.hlsInstance && !!this.isConnected) {
                    this.hlsInstance.startLoad();
                  }
                }, 1500);
                break; // Don't destroy if recovery works
              }
            } catch (error) {
              console.warn(
                "[LiveTV] Recovery attempt failed, will recreate instance:",
                error
              );
            }

            // If recovery didn't work, destroy and recreate
            this.destroyHlsInstance();
            const retryDelay = Math.min(
              this.retryDelay * (this.demuxerErrorCount + 1),
              5000
            ); // Reduced max delay
            console.log(`[LiveTV] Retrying HLS.js after ${retryDelay}ms`);
            setTimeout(() => {
              if (!!this.isConnected) {
                this.loadHlsJs();
              }
            }, retryDelay);
          } else {
            console.error(
              "[LiveTV] Max demuxer retries reached, trying fallback"
            );
            this.demuxerErrorCount = 0;
            this.reconnect();
          }
        } else {
          // Regular media error recovery
          try {
            if (this.hlsInstance) {
              this.hlsInstance.recoverMediaError();
            }
          } catch (error) {
            console.error("[LiveTV] Media error recovery failed:", error);
            this.reconnect();
          }
        }
        break;

      default:
        console.error("[LiveTV] Unrecoverable error, reconnecting");
        this.reconnect();
        break;
    }
  }

  /**
   * Non-fatal HLS.js errors are self-healing: the library nudges the playhead,
   * jumps the gap or re-requests the fragment without any help from us. On a
   * live stream a low-buffer stall is an expected hiccup, so logging every one
   * at error level only fills the console. Track them instead and act once a
   * stall actually persists.
   *
   * @param {string} details
   */
  handleNonFatalHlsError(details) {
    const isStall =
      details === "bufferStalledError" ||
      details === "bufferSeekOverHole" ||
      details === "bufferNudgeOnStall";

    if (!isStall) {
      console.warn("[LiveTV] HLS.js non-fatal error:", details);
      return;
    }

    // Stalls far apart in time are unrelated hiccups, not a stuck stream.
    const now = Date.now();
    if (now - this.lastStallTime > this.stallWindow) {
      this.stallCount = 0;
    }
    this.lastStallTime = now;
    this.stallCount++;

    if (this.stallCount < this.maxStallsBeforeRecovery) return;

    this.stallCount = 0;
    console.warn(
      `[LiveTV] Playback stalled ${this.maxStallsBeforeRecovery} times, resyncing to the live edge`
    );
    this.recoverFromStall();
  }

  /**
   * Repeated low-buffer stalls mean the playhead has crept ahead of what has
   * actually been downloaded. Drop back to the live sync position (only when
   * it is already buffered, so this never causes a visible rebuffer) and make
   * sure loading is running again.
   */
  recoverFromStall() {
    if (!this.hlsInstance || !this.videoElement || this.userPaused) return;

    const liveEdge = this.hlsInstance.liveSyncPosition;
    if (
      liveEdge != null &&
      Number.isFinite(liveEdge) &&
      Math.abs(liveEdge - this.videoElement.currentTime) > 1 &&
      this.isTimeBuffered(liveEdge)
    ) {
      this.videoElement.currentTime = liveEdge;
    }

    try {
      this.hlsInstance.startLoad();
    } catch (/** @type {unknown} */ error) {
      console.warn("[LiveTV] Stall recovery failed:", error);
    }
  }

  /**
   * @param {number} time
   * @returns {boolean} Whether the time sits inside an already buffered range.
   */
  isTimeBuffered(time) {
    if (!this.videoElement) return false;

    const ranges = this.videoElement.buffered;
    for (let i = 0; i < ranges.length; i++) {
      // Leave a margin at the end: the very edge of a range is where we stalled.
      if (time >= ranges.start(i) && time <= ranges.end(i) - 0.5) {
        return true;
      }
    }
    return false;
  }

  reconnect() {
    if (!this.isConnected || !this.videoElement) return;

    if (this.userPaused) {
      return;
    }

    // Prevent multiple simultaneous reconnection attempts
    if (this.isLoading) {
      console.warn("[LiveTV] Already reconnecting, skipping duplicate request");
      return;
    }

    // Check cooldown period
    const now = Date.now();
    if (now < this.cooldownUntil) {
      const remaining = Math.ceil((this.cooldownUntil - now) / 1000);
      console.warn(
        `[LiveTV] In cooldown period, ${remaining}s remaining. Skipping reconnect.`
      );
      return;
    }

    // Check throttling
    if (now - this.lastRetryTime < this.minRetryInterval) {
      const waitTime = this.minRetryInterval - (now - this.lastRetryTime);
      console.log(`[LiveTV] Throttling reconnect: waiting ${waitTime}ms`);
      setTimeout(() => this.reconnect(), waitTime);
      return;
    }

    // Clear any existing reconnect timer
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
    }

    // Check total retry limit
    if (this.totalRetryCount >= this.maxTotalRetries) {
      console.error(
        `[LiveTV] Max total retries (${this.maxTotalRetries}) reached. Entering cooldown.`
      );
      this.cooldownUntil = now + this.cooldownDuration;
      this.totalRetryCount = 0;
      this.loadFallback();
      return;
    }

    if (this.retryCount >= this.maxRetries) {
      console.error("[LiveTV] Max retries reached, trying fallback");
      this.loadFallback();
      return;
    }

    this.retryCount++;
    this.totalRetryCount++;
    const delay = Math.min(
      this.retryDelay * Math.min(this.retryCount, 5),
      10000
    ); // Max 10 seconds

    console.log(
      `[LiveTV] Reconnecting in ${delay}ms (attempt ${this.retryCount}/${this.maxRetries}, total: ${this.totalRetryCount}/${this.maxTotalRetries})`
    );

    this.reconnectTimer = setTimeout(() => {
      if (!this.isConnected || !this.videoElement) return;

      // Destroy current instance
      this.destroyHlsInstance();

      // Reset demuxer error count on reconnection attempt
      this.demuxerErrorCount = 0;

      // Reinitialize
      if (this.shouldUseNativeHls()) {
        this.loadNativeHLS();
      } else if (this.isHlsJsSupported()) {
        this.loadHlsJs();
      } else {
        this.loadFallback();
      }
    }, delay);
  }

  /**
   * @param {Event} event
   */
  handleVideoError(event) {
    // Prevent handling errors during loading or when restoring src
    if (this.isLoading || this.isRestoringSrc || this.errorHandlerDisabled) {
      if (this.isRestoringSrc) {
        // Silently ignore errors during src restoration
        return;
      }
      console.warn(
        "[LiveTV] Error during loading, will retry after load completes"
      );
      return;
    }

    console.error("[LiveTV] Video element error:", event);

    if (this.videoElement && this.videoElement.error) {
      const errorCode = this.videoElement.error.code;
      const errorMessage = this.videoElement.error.message || "";
      console.error(
        `[LiveTV] Video error code: ${errorCode}, message: ${errorMessage}`
      );

      // Check for empty src attribute error
      if (
        errorMessage.includes("Empty src attribute") ||
        (errorCode === 4 && !this.videoElement.src)
      ) {
        // Only restore if HLS.js is not managing the video
        if (this.hlsInstance) {
          console.warn(
            "[LiveTV] Empty src detected but HLS.js is managing video, ignoring"
          );
          return;
        }

        console.warn("[LiveTV] Empty src attribute detected, restoring source");
        this.isRestoringSrc = true;
        this.errorHandlerDisabled = true;

        // Restore source if we have one
        if (this.currentSrc) {
          this.videoElement.src = this.currentSrc;
        } else if (this.hlsUrl) {
          this.videoElement.src = this.hlsUrl;
          this.currentSrc = this.hlsUrl;
        }

        // Use requestAnimationFrame to ensure src is set before load
        requestAnimationFrame(() => {
          if (this.videoElement && this.videoElement.src) {
            this.videoElement.load();
            // Re-enable error handler after a short delay
            setTimeout(() => {
              this.isRestoringSrc = false;
              this.errorHandlerDisabled = false;
            }, 500);
          } else {
            this.isRestoringSrc = false;
            this.errorHandlerDisabled = false;
          }
        });
        return;
      }

      const isDemuxerError = this.isVideoDemuxerError(
        errorCode || 0,
        errorMessage
      );

      if (isDemuxerError) {
        if (this.isHlsJsSupported() && this.playbackMode !== "native") {
          this.handleVideoDemuxerWithHlsJs();
          return;
        }

        if (this.shouldUseNativeHls()) {
          const timeSinceInitialLoad = Date.now() - this.initialLoadStartTime;
          const inGracePeriod =
            this.isInitialLoad &&
            timeSinceInitialLoad < this.initialLoadGracePeriod;

          if (inGracePeriod) {
            console.log(
              `[LiveTV] Demuxer error during initial load (grace period: ${Math.ceil(
                (this.initialLoadGracePeriod - timeSinceInitialLoad) / 1000
              )}s remaining). This is normal for live streams.`
            );
            return;
          }

          this.demuxerErrorCount++;
          this.totalRetryCount++;
          console.warn(
            `[LiveTV] Native demuxer error (${this.demuxerErrorCount}/${this.maxDemuxerRetries}, total: ${this.totalRetryCount}/${this.maxTotalRetries})`
          );

          if (this.totalRetryCount >= this.maxTotalRetries) {
            this.cooldownUntil = Date.now() + this.cooldownDuration;
            this.totalRetryCount = 0;
            this.demuxerErrorCount = 0;
            this.loadFallback();
            return;
          }

          if (this.demuxerErrorCount < this.maxDemuxerRetries) {
            const retryDelay = Math.min(
              this.retryDelay * (this.demuxerErrorCount + 1),
              4000
            );
            console.log(
              `[LiveTV] Retrying native HLS after ${retryDelay}ms`
            );
            setTimeout(() => {
              if (this.isConnected) {
                this.loadNativeHLS();
              }
            }, retryDelay);
          } else {
            this.demuxerErrorCount = 0;
            this.loadFallback();
          }
          return;
        }

        if (this.isHlsJsSupported()) {
          this.handleVideoDemuxerWithHlsJs();
          return;
        }

        this.loadFallback();
        return;
      }
    }

    // Try to recover for other errors
    this.totalRetryCount++;
    if (this.totalRetryCount >= this.maxTotalRetries) {
      console.error(
        `[LiveTV] Max total retries (${this.maxTotalRetries}) reached. Entering cooldown.`
      );
      this.cooldownUntil = Date.now() + this.cooldownDuration;
      this.totalRetryCount = 0;
      this.loadFallback();
    } else if (this.retryCount < this.maxRetries) {
      this.reconnect();
    } else {
      this.loadFallback();
    }
  }

  handleVideoStalled() {
    if (!this.videoElement || !this.isPlaying || this.userPaused) {
      return;
    }

    if (this.videoElement.paused && this.videoElement.src) {
      console.warn("[LiveTV] Video stalled, attempting to resume");
      this.videoElement
        .play()
        .then(() => {
          this.isPlaying = true;
        })
        .catch((/** @type {Error} */ error) => {
          console.error("[LiveTV] Failed to resume playback:", error);
          this.isPlaying = false;
        });
    }
  }

  handleVideoWaiting() {
    // Video is buffering, this is normal for live streams
    // Reduce log noise - only log first few times or if there are errors
    this.bufferingLogCount++;
    if (this.bufferingLogCount <= 2 || this.errorCount > 0) {
      if (this.isInitialLoad) {
        console.log(
          "[LiveTV] Video buffering during initial load (this is normal)..."
        );
      } else if (this.bufferingLogCount <= 2) {
        console.log("[LiveTV] Video buffering...");
      }
    }
    // Reset counter after a while to allow occasional logging
    if (this.bufferingLogCount > 10) {
      this.bufferingLogCount = 0;
    }
  }

  handleOnline() {
    console.log("[LiveTV] Network connection restored");

    // Reset error count
    this.errorCount = 0;

    if (
      this.videoElement &&
      this.isPlaying &&
      !this.userPaused &&
      (this.videoElement.paused || this.videoElement.readyState < 2)
    ) {
      this.reconnect();
    }
  }

  handleOffline() {
    console.warn("[LiveTV] Network connection lost");
  }



  async loadFallback() {
    if (!this.isConnected || !this.streamStarted) return;
    if (!this.videoElement) {
      console.error("[LiveTV] Cannot load fallback: missing video element");
      return;
    }

    // Prevent multiple simultaneous fallback loads
    if (this.isLoading) {
      console.warn(
        "[LiveTV] Already loading fallback, skipping duplicate request"
      );
      return;
    }

    this.isLoading = true;
    const streamSession = this._streamSession;
    this.playbackMode = "fallback";
    console.log("[LiveTV] Loading fallback stream");

    // Destroy HLS instance if exists
    this.destroyHlsInstance();

    // Only clear source if needed
    const needsClear =
      !this.videoElement.src ||
      (this.dashUrl && this.videoElement.src !== this.dashUrl) ||
      (this.hlsUrl && this.videoElement.src !== this.hlsUrl && !this.dashUrl) ||
      (!this.isPlaying && this.videoElement.readyState === 0);

    if (needsClear) {
      this.videoElement.src = "";
      this.videoElement.load();
      // Reduced wait time
      await new Promise((resolve) => setTimeout(resolve, 200));
    }
    if (!this.isConnected || streamSession !== this._streamSession) return;

    // Reset error counts but keep total retry count for cooldown tracking
    this.demuxerErrorCount = 0;
    this.retryCount = 0;
    this.errorCount = 0;

    if (this.isHlsJsSupported() && this.hlsUrl) {
      console.log("[LiveTV] Using HLS.js after fallback (avoid direct src on Chromium)");
      this.isLoading = false;
      this.loadHlsJs();
      return;
    }

    if (this.dashUrl) {
      this.videoElement.src = this.dashUrl;
      this.currentSrc = this.dashUrl;
      console.log("[LiveTV] Using DASH fallback");
    } else if (this.hlsUrl && this.shouldUseNativeHls()) {
      this.videoElement.src = this.hlsUrl;
      this.currentSrc = this.hlsUrl;
      console.log("[LiveTV] Using direct HLS fallback (Safari)");
    } else if (this.hlsUrl) {
      console.error(
        "[LiveTV] HLS fallback unavailable in this browser (no HLS.js)"
      );
      this.isLoading = false;
      return;
    } else {
      console.error("[LiveTV] No fallback URLs available");
      this.isLoading = false;
      return;
    }

    // Add error handler for fallback
    const onError = () => {
      console.error("[LiveTV] Fallback stream also failed");
      this.isLoading = false;
      if (this.videoElement) {
        this.videoElement.removeEventListener("error", onError);
      }
      // Don't retry fallback - user intervention needed
    };

    const onLoadedMetadata = () => {
      console.log("[LiveTV] Fallback stream loaded successfully");
      this.isLoading = false;
      this.isPlaying = false; // Will be set to true when play() succeeds
      // Reset counters on success
      this.totalRetryCount = 0;
      this.cooldownUntil = 0;
      if (this.videoElement) {
        this.videoElement.removeEventListener(
          "loadedmetadata",
          onLoadedMetadata
        );
        if (this.autoplay || this._pendingUserPlay) {
          this._attemptPendingPlayback();
        }
      }
    };

    this.videoElement.addEventListener("error", onError, { once: true });
    this.videoElement.addEventListener("loadedmetadata", onLoadedMetadata, {
      once: true,
    });
  }

  destroyHlsInstance() {
    // Cancel any pending requests
    this.cancelPendingRequests();

    if (this.hlsInstance) {
      try {
        // Destroy HLS instance (this will stop loading automatically)
        this.hlsInstance.destroy();
      } catch (error) {
        console.error("[LiveTV] Error destroying HLS instance:", error);
      }
      this.hlsInstance = null;
    }
  }

    destroy() {
      if (!this.isInitialized) return;

      this._streamSession++;
      this.isInitialized = false;
      this.isLoading = false;
      this.streamStarted = false;
      this._pendingUserPlay = false;
      clearInterval(this._hlsWaitTimer);
      this._hlsWaitTimer = null;
      if (this._nativeLoadCleanup) this._nativeLoadCleanup();

      this.cancelPendingRequests();

      if (this.reconnectTimer !== null) {
        clearTimeout(this.reconnectTimer);
        this.reconnectTimer = null;
      }

      window.removeEventListener("online", this.handleOnline);
      window.removeEventListener("offline", this.handleOffline);

      this.destroyHlsInstance();

      if (this.videoElement) {
        this.videoElement.removeEventListener("play", this.handlePlaybackPlay);
        this.videoElement.removeEventListener("pause", this.handlePlaybackPause);
        this.videoElement.removeEventListener("error", this._boundVideoError);
        this.videoElement.removeEventListener("stalled", this._boundVideoStalled);
        this.videoElement.removeEventListener("waiting", this._boundVideoWaiting);
        this.videoElement.pause();
        this.videoElement.removeAttribute("src");
        this.videoElement.load();
      }

      console.log("[LiveTV] Player destroyed");
    }
  }

  if (!customElements.get("live-tv-player")) {
    customElements.define("live-tv-player", LiveTvPlayer);
  }
})();
